import { useRef, type KeyboardEvent, type ReactNode } from 'react'

import { panelId, tabId } from './tabIds'

export interface TabItem {
  id: string
  label: string
  icon?: ReactNode
  /** Число рядом с подписью (сколько записей во вкладке); `undefined` — без числа. */
  badge?: number
  disabled?: boolean
  /** Пояснение во всплывающей подсказке — например, почему вкладка недоступна. */
  hint?: string
}

/**
 * Полоса вкладок внутри экрана (WAI-ARIA tabs): `role="tablist"`, у выбранной
 * `aria-selected` и единственный `tabIndex=0`, стрелки/Home/End двигают выбор.
 * Панель содержимого рисует вызывающий: id панели — `panelId(tab)`, ей нужны
 * `role="tabpanel"` и `aria-labelledby={tabId(tab)}`. Выбранная вкладка
 * отличается полосой и жирностью, а не только цветом (CLAUDE.md §7).
 *
 * Для вкладок-адресов (ссылки разделов) — та же разметка `.krp-tabs`/`.krp-tab`
 * на `<nav>` со ссылками и `aria-current="page"`, без этого компонента.
 */
export function Tabs({
  label,
  items,
  selected,
  onSelect,
  idPrefix,
  variant = 'line',
}: {
  /** Имя полосы для диктора: «Разделы поставки». */
  label: string
  items: readonly TabItem[]
  selected: string
  onSelect: (id: string) => void
  /** Префикс id вкладок и панелей; уникален на странице. */
  idPrefix: string
  variant?: 'line' | 'pill'
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>())
  const enabled = items.filter((item) => item.disabled !== true)

  const move = (event: KeyboardEvent<HTMLButtonElement>, from: string) => {
    const at = enabled.findIndex((item) => item.id === from)
    if (at < 0 || enabled.length === 0) return
    const next =
      event.key === 'ArrowRight'
        ? enabled[(at + 1) % enabled.length]
        : event.key === 'ArrowLeft'
          ? enabled[(at - 1 + enabled.length) % enabled.length]
          : event.key === 'Home'
            ? enabled[0]
            : event.key === 'End'
              ? enabled[enabled.length - 1]
              : undefined
    if (next === undefined) return
    event.preventDefault()
    onSelect(next.id)
    refs.current.get(next.id)?.focus()
  }

  return (
    <div className="krp-tabs" role="tablist" aria-label={label} data-variant={variant === 'pill' ? 'pill' : undefined}>
      {items.map((item) => {
        const active = item.id === selected
        return (
          <button
            key={item.id}
            ref={(node) => {
              if (node === null) refs.current.delete(item.id)
              else refs.current.set(item.id, node)
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, item.id)}
            className="krp-tab"
            aria-selected={active}
            aria-controls={panelId(idPrefix, item.id)}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            title={item.hint}
            onClick={() => onSelect(item.id)}
            onKeyDown={(event) => move(event, item.id)}
          >
            {item.icon}
            {item.label}
            {/* Пробел перед числом — для диктора: «1С Основная 12», а не «1С Основная12». */}
            {item.badge !== undefined && (
              <>
                {' '}
                <span className="krp-tab__badge">{item.badge}</span>
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}
